import React, { useCallback, useEffect, useState } from "react";
import { FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import {
  atualizarVeiculo,
  criarVeiculo,
  desativarVeiculo,
  listarVeiculos,
  TIPOS_VEICULO,
} from "../../../services/logistica";

import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Input,
  Pill,
  SecondaryButton,
  SectionTitle,
  Select,
  Table,
  TableWrapper,
} from "../../Orcamentos/styles";


function VeiculoForm({ inicial, onSave, onCancel, unidadeId }) {
  const [dados, setDados] = useState({
    placa: inicial?.placa || "",
    descricao: inicial?.descricao || "",
    tipo: inicial?.tipo || "VAN",
    capacidade_kg: inicial?.capacidade_kg || 0,
    capacidade_m3: inicial?.capacidade_m3 || "0",
    proprio: inicial?.proprio !== undefined ? inicial.proprio : true,
  });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Placa *</span>
        <Input value={dados.placa} onChange={(e) => setDados({ ...dados, placa: e.target.value.toUpperCase() })} maxLength={10} required />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Descrição</span>
        <Input value={dados.descricao} onChange={(e) => setDados({ ...dados, descricao: e.target.value })} maxLength={120}
               placeholder="Ex: Iveco Daily 2020 Branco" />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Tipo *</span>
        <Select value={dados.tipo} onChange={(e) => setDados({ ...dados, tipo: e.target.value })}>
          {TIPOS_VEICULO.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </Select>
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Capacidade (kg)</span>
        <Input type="number" min="0" value={dados.capacidade_kg}
               onChange={(e) => setDados({ ...dados, capacidade_kg: Number(e.target.value) })} />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Capacidade (m³)</span>
        <Input type="number" step="0.01" min="0" value={dados.capacidade_m3}
               onChange={(e) => setDados({ ...dados, capacidade_m3: e.target.value })} />
      </label>
      <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <input type="checkbox" checked={dados.proprio}
               onChange={(e) => setDados({ ...dados, proprio: e.target.checked })} />
        <span style={{ fontSize: 14 }}>Veículo próprio (desmarcado = alugado)</span>
      </label>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <SecondaryButton onClick={onCancel}>Cancelar</SecondaryButton>
        <AsyncButton
          variant="primary"
          onClick={() => onSave({ ...dados, unidade: unidadeId })}
          successMessage="Veículo salvo"
        >
          Salvar
        </AsyncButton>
      </div>
    </div>
  );
}


export default function VeiculosTab() {
  const { request, hasAllPermissions, isMaster } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editando, setEditando] = useState(null);
  const [unidadeId, setUnidadeId] = useState("");

  const podeManage = isMaster || hasAllPermissions(["veiculo:manage"]);

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = await listarVeiculos(request);
      const list = Array.isArray(p) ? p : p?.results || [];
      setItems(list);
      if (list.length > 0 && !unidadeId) setUnidadeId(list[0].unidade);
    } catch (err) {
      setError(err?.message || "Erro.");
    } finally { setLoading(false); }
  }, [request, unidadeId]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    if (unidadeId) return;
    request("/api/unidades").then((d) => {
      const list = Array.isArray(d) ? d : d?.results || [];
      if (list.length > 0) setUnidadeId(list[0].id);
    }).catch(() => {});
  }, [request, unidadeId]);

  const handleSalvar = async (dados) => {
    if (editando === "novo") await criarVeiculo(request, dados);
    else await atualizarVeiculo(request, editando.id, dados);
    setEditando(null);
    await carregar();
  };

  const handleExcluir = async (v) => {
    if (!window.confirm(`Desativar veículo "${v.placa}"?`)) return;
    await desativarVeiculo(request, v.id);
    await carregar();
  };

  return (
    <>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <SectionTitle>{loading ? "Carregando..." : `${items.length} veículo(s)`}</SectionTitle>
          {podeManage && (
            <SecondaryButton onClick={() => setEditando("novo")}>
              <FiPlus size={14} /> Novo
            </SecondaryButton>
          )}
        </div>
        {error && <ErrorMsg>{error}</ErrorMsg>}
        {!loading && items.length === 0 && <EmptyMsg>Nenhum veículo.</EmptyMsg>}
        {items.length > 0 && (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>Placa</th>
                  <th>Tipo</th>
                  <th>Descrição</th>
                  <th>Capacidade</th>
                  <th>Próprio</th>
                  <th>Status</th>
                  {podeManage && <th>Ações</th>}
                </tr>
              </thead>
              <tbody>
                {items.map((v) => (
                  <tr key={v.id}>
                    <td><strong>{v.placa}</strong></td>
                    <td>{v.tipo_display}</td>
                    <td>{v.descricao || "—"}</td>
                    <td>{v.capacidade_kg}kg / {v.capacidade_m3}m³</td>
                    <td>{v.proprio ? "Sim" : "Alugado"}</td>
                    <td>
                      <Pill $tone={v.ativo ? "success" : "neutral"}>
                        {v.ativo ? "Ativo" : "Inativo"}
                      </Pill>
                    </td>
                    {podeManage && (
                      <td>
                        <div style={{ display: "flex", gap: 4 }}>
                          <SecondaryButton onClick={() => setEditando(v)} style={{ padding: "4px 8px", fontSize: 12 }}>
                            <FiEdit2 size={12} />
                          </SecondaryButton>
                          {v.ativo && (
                            <SecondaryButton onClick={() => handleExcluir(v)} style={{ padding: "4px 8px", fontSize: 12 }}>
                              <FiTrash2 size={12} />
                            </SecondaryButton>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>

      {editando && (
        <SlideOverPanel
          open
          onClose={() => setEditando(null)}
          title={editando === "novo" ? "Novo veículo" : `Editar ${editando.placa}`}
        >
          <VeiculoForm
            inicial={editando === "novo" ? null : editando}
            onSave={handleSalvar}
            onCancel={() => setEditando(null)}
            unidadeId={unidadeId}
          />
        </SlideOverPanel>
      )}
    </>
  );
}
